/**
 * Route definitions. Adding a route here is what adds it to the spec.
 *
 * Naming, settled once because renaming a public resource is expensive:
 *   - `triggers`  the RULE that grants a reward
 *   - `activity`  the immutable log of what happened
 *   - `members`   a user's relationship to ONE channel (a user is an account)
 * The predecessors used "events" for both the rule and the arrival, which is
 * why `api/admin/events.ts` and `webhooks/eventHandlers.ts` mean different
 * things there.
 */
import { z } from 'zod';
import { route } from './registry.js';
import { Channel, ChannelPatch } from './schemas/channel.js';
import { ChannelId, Created, Ok, PageQuery, pageOf } from './schemas/common.js';
import {
  Asset, AssetPatch, AssetUpload, AssetUploadRequest, Bundle,
} from './schemas/catalog.js';
import { Reward, RewardFields, RewardInput } from './schemas/reward.js';
import { BundleCapacity, SimulateRequest, SimulationResult } from './schemas/packs.js';
import { LoginStart, LoginStarted, Session, ChatCredential } from './schemas/session.js';
import { ConnectStart, TwitchConnection, TwitchConnectionPatch } from './schemas/twitchConnection.js';
import {
  ChannelModerator, ChannelModeratorList, ChannelModeratorPatch, Moderation,
} from './schemas/moderation.js';
import { AutomodDecision, BannedList, ModActionInput } from './schemas/modFeed.js';
import { Instance } from './schemas/instance.js';
import { EmoteOverrideList, SavedEmoteList, SenderEmotes, SenderEmotesQuery } from './schemas/savedEmotes.js';
import { Trade, TradeConfirm, TradeCreate, TradeOfferInput } from './schemas/trade.js';
import { RarityKey, RarityTier, RarityTierInput } from './schemas/rarity.js';
import {
  Room, RoomAdmin, RoomAdminInput, RoomChannel, RoomId, RoomInput, RoomPatch,
  RoomPresence, SearchResults,
} from './schemas/room.js';
import {
  RoomFeedDeleted, RoomFeedHello, RoomFeedMessage,
  RoomMessage, RoomMessageId, RoomMessageInput, RoomMute, RoomMuteInput,
} from './schemas/roomChat.js';
import {
  DiscordHook, DiscordHookId, DiscordHookInput, DiscordHookList, DiscordHookPatch, DiscordHookTest,
} from './schemas/discord.js';
import { RoomLiveCounts, RoomRoster } from './schemas/roster.js';
import { RoomGroupings, RoomGroupingsInput } from './schemas/grouping.js';
import { TwitchCategoryList } from './schemas/room.js';
import { BugReport, BugReportInput } from './schemas/bugReport.js';
import {
  Item, ItemBuildPreview, ItemBuildRequest, ItemInput, ItemPatch,
} from './schemas/item.js';
import { Trigger } from './schemas/triggers.js';
import {
  BundleFields, BundleInput, TriggerFields, TriggerInput,
} from './schemas/catalogInput.js';

const channelParams = z.object({ channel: ChannelId });

route({
  method: 'get',
  path: '/v1/channels/{channel}',
  operationId: 'getChannel',
  summary: 'Fetch a channel',
  tags: ['channels'],
  scopes: ['channel:read'],
  params: channelParams,
  response: Channel,
  errors: [401, 403, 404, 429],
});

route({
  method: 'patch',
  path: '/v1/channels/{channel}',
  operationId: 'updateChannel',
  summary: 'Update a channel',
  tags: ['channels'],
  scopes: ['channel:write'],
  params: channelParams,
  body: ChannelPatch,
  response: Channel,
  errors: [400, 401, 403, 404, 409, 429],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/channels/{channel}/twitch',
  operationId: 'getTwitchConnection',
  summary: "A channel's link to Twitch: consented scopes, and whether mod tools are on",
  tags: ['channels'],
  scopes: ['channel:write'],
  params: channelParams,
  response: TwitchConnection,
  errors: [401, 403, 404, 429],
});

route({
  method: 'patch',
  path: '/v1/channels/{channel}/twitch',
  operationId: 'updateTwitchConnection',
  summary: "Turn this channel's mod tools on tomo on or off",
  tags: ['channels'],
  scopes: ['channel:write'],
  params: channelParams,
  body: TwitchConnectionPatch,
  response: TwitchConnection,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/modtools/automod/{message_id}',
  operationId: 'decideAutomodMessage',
  summary: 'Allow a held AutoMod message into chat, or deny it, as you',
  tags: ['modtools'],
  scopes: ['modtools:write'],
  params: z.object({ channel: ChannelId, message_id: z.string().min(1).max(128) }),
  body: AutomodDecision,
  response: Ok,
  errors: [400, 401, 403, 404, 409, 429, 501, 503],
});

route({
  method: 'get',
  path: '/v1/channels/{channel}/modtools/banned',
  operationId: 'listBannedForModerator',
  summary: 'Who is banned or timed out on this channel, read from Twitch as you',
  tags: ['modtools'],
  scopes: ['modtools:read'],
  params: channelParams,
  response: BannedList,
  errors: [401, 403, 404, 429, 503],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/modtools/actions',
  operationId: 'takeModAction',
  summary: 'Ban, time out, delete, warn, change chat modes and more, on Twitch, as you',
  tags: ['modtools'],
  scopes: ['modtools:write'],
  params: channelParams,
  body: ModActionInput,
  response: Ok,
  errors: [400, 401, 403, 404, 409, 422, 429, 501, 503],
});

route({
  method: 'get',
  path: '/v1/channels/{channel}/moderators',
  operationId: 'listChannelModerators',
  summary: "The channel's Twitch moderators who use tomo, and their standing here",
  tags: ['channels'],
  scopes: ['channel:write'],
  params: channelParams,
  response: ChannelModeratorList,
  errors: [401, 403, 404, 429],
});

route({
  method: 'patch',
  path: '/v1/channels/{channel}/moderators/{uid}',
  operationId: 'updateChannelModerator',
  summary: "Suspend a moderator's tomo mod tools on this channel, or lift it",
  tags: ['channels'],
  scopes: ['channel:write'],
  params: z.object({ channel: ChannelId, uid: z.string().min(1).max(128) }),
  body: ChannelModeratorPatch,
  response: ChannelModerator,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});


route({
  method: 'get',
  path: '/v1/rosters/{channel}',
  operationId: 'getRoster',
  summary: 'The channel’s curated roster of friends-of-the-channel',
  tags: ['discovery'],
  scopes: ['channel:read'],
  params: channelParams,
  query: PageQuery,
  response: pageOf(Channel),
  errors: [401, 403, 404, 429],
});

// --- catalog authoring ------------------------------------------------------
//
// Registered here, not just wired into Fastify. check-routes compares the
// SERVER's route table to the published spec, and these three shipped
// undocumented because registration was a separate step someone could skip.

/**
 * Four verbs per resource, registered from one table.
 *
 * Written as a loop rather than 27 route() calls, because the alternative is
 * three near-identical blocks that drift one field at a time — which is how
 * three catalog routes came to be served and undocumented in the first place.
 *
 * DELETE is absent, and stays absent. A catalog entry that has minted anything
 * is referenced by instances forever; `active: false` takes it out of every
 * pool without stranding what it produced (L9 generalised from instances to
 * the things that make them).
 */
const CATALOG = [
  {
    segment: 'rewards', noun: 'reward',
    input: RewardInput, patch: RewardFields.partial(), wire: Reward,
    readScope: 'rewards:read' as const,
  },
  {
    segment: 'bundles', noun: 'bundle',
    input: BundleInput, patch: BundleFields.partial(), wire: Bundle,
    readScope: 'rewards:read' as const,
  },
  {
    // A trigger is the payout RULE. Listing them tells a viewer exactly what
    // to do to be paid, so reading them takes the authoring scope rather than
    // the viewer-facing `rewards:read`.
    segment: 'triggers', noun: 'trigger',
    input: TriggerInput, patch: TriggerFields.partial(), wire: Trigger,
    readScope: 'rewards:write' as const,
  },
];

for (const c of CATALOG) {
  const collection = `/v1/channels/{channel}/${c.segment}`;
  const item = `${collection}/{id}`;
  const Title = c.noun.charAt(0).toUpperCase() + c.noun.slice(1);
  const itemParams = z.object({ channel: ChannelId, id: z.string() });

  route({
    method: 'get',
    path: collection,
    operationId: `list${Title}s`,
    summary: `List this channel’s ${c.segment}`,
    tags: ['catalog'],
    scopes: [c.readScope],
    params: channelParams,
    query: PageQuery,
    response: pageOf(c.wire),
    errors: [400, 401, 403, 404, 429],
  });

  route({
    method: 'get',
    path: item,
    operationId: `get${Title}`,
    summary: `Fetch a ${c.noun}`,
    tags: ['catalog'],
    scopes: [c.readScope],
    params: itemParams,
    response: c.wire,
    errors: [401, 403, 404, 429],
  });

  route({
    method: 'post',
    path: collection,
    operationId: `create${Title}`,
    summary: `Create a ${c.noun}`,
    tags: ['catalog'],
    scopes: ['rewards:write'],
    params: channelParams,
    body: c.input,
    response: Created,
    errors: [400, 401, 403, 404, 409, 429],
    idempotent: true,
  });

  route({
    method: 'patch',
    path: item,
    operationId: `update${Title}`,
    // L16 — the patch is merged onto the current resource and the result is
    // validated by the SAME schema create uses. Theirs was weaker on update
    // and could save a valid bundle back broken.
    summary: `Update a ${c.noun}`,
    tags: ['catalog'],
    scopes: ['rewards:write'],
    params: itemParams,
    // The BODY is any subset of the fields; what gets VALIDATED is the merged
    // result, against `input` above. Publishing `input` here would tell an
    // integrator every field is required on a patch, which is not true.
    body: c.patch,
    response: c.wire,
    errors: [400, 401, 403, 404, 409, 429],
    idempotent: true,
  });
}

/**
 * What a bundle can do right now, and what it would actually hand out.
 *
 * Both read-only, and both computed by the SAME selector that mints — which
 * is the whole point. stefancoat's equivalents live in the browser over a
 * per-slot model that `build-bundle-from-box` discards before it reaches the
 * database, so the preview it draws predicts nothing about production.
 */
route({
  method: 'get',
  path: '/v1/channels/{channel}/bundles/{id}/capacity',
  operationId: 'getBundleCapacity',
  summary: 'Whether a bundle can fill, and how many whole packs are left',
  tags: ['catalog'],
  scopes: ['rewards:read'],
  params: z.object({ channel: ChannelId, id: z.string() }),
  response: BundleCapacity,
  errors: [401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/bundles/{id}/simulate',
  operationId: 'simulateBundle',
  summary: 'Open a bundle N times without writing anything',
  tags: ['catalog'],
  // Authoring, not reading: it is a design tool, and running ten thousand
  // draws is not something a viewer-facing token should be able to ask for.
  scopes: ['rewards:write'],
  params: z.object({ channel: ChannelId, id: z.string() }),
  body: SimulateRequest,
  response: SimulationResult,
  errors: [400, 401, 403, 404, 422, 429],
});

// --- signing in ------------------------------------------------------------
//
// Three endpoints, and the shape of them is the security design: `state` is an
// opaque id pointing at a server-side document, `return_to` is a same-origin
// PATH, and the session lands in a __Host- cookie the browser will not let a
// subdomain set for its parent.

route({
  method: 'post',
  path: '/v1/auth/start',
  operationId: 'startLogin',
  summary: 'Begin a Twitch sign-in',
  tags: ['auth'],
  // Nobody is signed in yet, by definition.
  scopes: [],
  body: LoginStart,
  response: LoginStarted,
  errors: [400, 429],
});

route({
  method: 'post',
  path: '/v1/auth/twitch/connect',
  operationId: 'startChannelConnect',
  summary: 'Begin connecting the Twitch channel you broadcast',
  tags: ['auth'],
  // A signed-in person, and no tomo scope: what they are asking to connect is
  // their OWN Twitch channel, and the consent screen is the proof of that.
  scopes: [],
  body: ConnectStart,
  response: LoginStarted,
  errors: [400, 401, 429],
});

route({
  method: 'post',
  path: '/v1/auth/twitch/moderate',
  operationId: 'startModerating',
  summary: 'Begin granting tomo the moderator consent, to moderate from tomo',
  tags: ['auth'],
  // The person's OWN moderator authority, which Twitch grants and Twitch
  // checks. No tomo scope describes it.
  scopes: [],
  body: ConnectStart,
  response: LoginStarted,
  errors: [400, 401, 429, 501],
});

route({
  method: 'get',
  path: '/v1/me/moderation',
  operationId: 'getMyModeration',
  summary: 'The channels you moderate on Twitch, and whether tomo mod tools are yours there',
  tags: ['auth'],
  scopes: [],
  response: Moderation,
  errors: [401, 409, 429, 501],
});

route({
  method: 'post',
  path: '/v1/auth/twitch/chat-credential',
  operationId: 'chatCredential',
  summary: "A short-lived Twitch token for the browser's own chat connection",
  tags: ['auth'],
  // No scope: this asks for the viewer's OWN credential, and the session
  // already says who that is. A tomo scope would be describing a permission
  // over somebody else's data, which this is not.
  scopes: [],
  // POST despite reading nothing: it mints a credential and rotates the stored
  // refresh token as a side effect, so it is neither safe nor idempotent, and
  // it must never be cached or prefetched.
  body: z.object({}).optional(),
  response: ChatCredential,
  // 409: signed in, but the stored credential predates chat scopes or has been
  // revoked at Twitch. Distinct from 401 because the SESSION is fine — it is
  // the Twitch link that needs redoing, and the client should say so rather
  // than bouncing the viewer through a login that will not fix it.
  errors: [401, 409, 429],
});

route({
  method: 'get',
  path: '/v1/auth/twitch/callback',
  operationId: 'completeLogin',
  summary: 'Where Twitch returns the browser. Sets the session and redirects.',
  tags: ['auth'],
  scopes: [],
  query: z.object({ code: z.string().optional(), state: z.string().optional() }),
  response: z.object({}),
  redirect: true,
  errors: [400, 401, 429],
});

route({
  method: 'get',
  path: '/v1/auth/session',
  operationId: 'getSession',
  summary: 'Who is signed in, and what they may administer',
  tags: ['auth'],
  scopes: [],
  response: Session,
  errors: [401, 429],
});

route({
  method: 'delete',
  path: '/v1/auth/session',
  operationId: 'endSession',
  summary: 'Sign out, everywhere',
  tags: ['auth'],
  scopes: [],
  response: Ok,
  errors: [401, 429],
});

// --- minted copies -----------------------------------------------------------

route({
  method: 'get',
  path: '/v1/channels/{channel}/instances',
  operationId: 'listChannelInstances',
  summary: 'Everything this channel has minted',
  tags: ['instances'],
  scopes: ['instances:read'],
  params: channelParams,
  query: PageQuery,
  response: pageOf(Instance),
  errors: [400, 401, 403, 404, 429, 503],
});

/**
 * No `{uid}` in the path, and there never will be: the only person whose
 * inventory you can read is you. A route shaped `/members/{uid}/instances`
 * puts the authorization decision in every handler, and one handler
 * eventually forgets.
 */
route({
  method: 'get',
  path: '/v1/me/instances',
  operationId: 'listMyInstances',
  summary: 'Your collection, across every channel',
  tags: ['instances'],
  scopes: ['inventory:read'],
  query: PageQuery.extend({ channel: ChannelId.optional() }),
  response: pageOf(Instance),
  errors: [400, 401, 403, 429, 503],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/instances/{id}/reveal',
  operationId: 'revealInstance',
  // L29 — a transactional idempotent no-op, not a toggle. Opening something
  // already open answers with the same item, not an error: a streamer
  // double-clicking on camera must not see a failure.
  summary: 'Open a wrapped item you own',
  tags: ['instances'],
  scopes: ['inventory:write'],
  params: z.object({ channel: ChannelId, id: z.string() }),
  response: Instance,
  errors: [400, 401, 403, 404, 409, 429],
  idempotent: true,
});

// --- trading -----------------------------------------------------------------
//
// Not under /v1/channels: a trade is the cross-tenant object by definition,
// and putting it under one channel would mean picking which participant's
// channel owns the negotiation.

const tradeParams = z.object({ id: z.string() });

route({
  method: 'get',
  path: '/v1/trades',
  operationId: 'listTrades',
  summary: 'Trades you are part of',
  tags: ['trades'],
  scopes: ['trades:read'],
  response: pageOf(Trade),
  errors: [401, 403, 429],
});

route({
  method: 'post',
  path: '/v1/trades',
  operationId: 'createTrade',
  summary: 'Open a negotiation',
  tags: ['trades'],
  scopes: ['trades:write'],
  body: TradeCreate,
  response: Trade,
  errors: [400, 401, 403, 429],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/trades/{id}',
  operationId: 'getTrade',
  summary: 'One trade',
  tags: ['trades'],
  scopes: ['trades:read'],
  params: tradeParams,
  response: Trade,
  errors: [401, 403, 404, 429],
});

route({
  method: 'put',
  path: '/v1/trades/{id}/offers',
  operationId: 'setTradeOffers',
  // Wholesale, never a partial merge. A participant who believes they
  // withdrew an item and did not is the worst failure on a screen about trust.
  summary: 'Replace your whole side of a trade',
  tags: ['trades'],
  scopes: ['trades:write'],
  params: tradeParams,
  body: TradeOfferInput,
  response: Trade,
  errors: [400, 401, 403, 404, 409, 429],
});

route({
  method: 'post',
  path: '/v1/trades/{id}/confirm',
  operationId: 'confirmTrade',
  // You confirm a HASH, not a trade id. Any edit to any side changes it and
  // invalidates every confirmation — the fix for a last-second swap, and the
  // shape of the data rather than a check somebody remembers.
  summary: 'Agree to the current contents. Settles when everyone has.',
  tags: ['trades'],
  scopes: ['trades:write'],
  params: tradeParams,
  body: TradeConfirm,
  response: Trade,
  errors: [400, 401, 403, 404, 409, 429],
  idempotent: true,
});

route({
  method: 'post',
  path: '/v1/trades/{id}/cancel',
  operationId: 'cancelTrade',
  summary: 'Walk away, releasing every lock',
  tags: ['trades'],
  scopes: ['trades:write'],
  params: tradeParams,
  response: Trade,
  errors: [401, 403, 404, 409, 429],
  idempotent: true,
});

// --- rarity tiers ------------------------------------------------------------
//
// A streamer's own ladder. There is no DELETE: a tier is retired and never
// removed, because a copy stamped with it must resolve forever.

const tierParams = z.object({ channel: ChannelId, key: RarityKey });

route({
  method: 'get',
  path: '/v1/channels/{channel}/rarity-tiers',
  operationId: 'listRarityTiers',
  summary: 'This channel\u2019s rarity ladder, least rare first',
  tags: ['catalog'],
  scopes: ['rewards:read'],
  params: channelParams,
  query: z.object({ include_retired: z.enum(['true', 'false']).optional() }),
  response: z.object({ data: z.array(RarityTier) }),
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'put',
  path: '/v1/channels/{channel}/rarity-tiers/{key}',
  operationId: 'putRarityTier',
  summary: 'Create or edit a rarity tier',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: tierParams,
  body: RarityTierInput,
  response: RarityTier,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/rarity-tiers/{key}/retire',
  operationId: 'retireRarityTier',
  // Retire, not delete. Deleting would strand every copy carrying the tier.
  summary: 'Stop offering a tier, without breaking the copies that carry it',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: tierParams,
  body: z.object({ retired: z.boolean() }),
  response: RarityTier,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

// --- assets ------------------------------------------------------------------
//
// Two calls with the bytes going straight to the bucket in between. The second
// is where the file is identified, by reading it rather than believing it.

const assetParams = z.object({ channel: ChannelId, id: z.string() });

route({
  method: 'get',
  path: '/v1/channels/{channel}/assets',
  operationId: 'listAssets',
  summary: 'Files this channel has uploaded',
  tags: ['assets'],
  scopes: ['assets:read'],
  params: channelParams,
  query: z.object({ limit: z.coerce.number().int().min(1).max(200).optional() }),
  response: z.object({ data: z.array(Asset) }),
  errors: [400, 401, 403, 404, 429, 503],
});

route({
  method: 'get',
  path: '/v1/channels/{channel}/assets/{id}',
  operationId: 'getAsset',
  summary: 'One file',
  tags: ['assets'],
  scopes: ['assets:read'],
  params: assetParams,
  response: Asset,
  errors: [401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/assets',
  operationId: 'reserveAsset',
  summary: 'Reserve an asset and get somewhere to PUT the bytes',
  tags: ['assets'],
  scopes: ['assets:write'],
  params: channelParams,
  body: AssetUploadRequest,
  response: AssetUpload,
  errors: [400, 401, 403, 404, 415, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/assets/{id}/complete',
  operationId: 'completeAsset',
  // The bytes decide. A declared content type, a filename and a Content-Type
  // header are all attacker-controlled; what landed in the bucket is not.
  summary: 'Identify the uploaded bytes and accept or destroy them',
  tags: ['assets'],
  scopes: ['assets:write'],
  params: assetParams,
  response: Asset,
  errors: [400, 401, 403, 404, 415, 429],
  idempotent: true,
});

route({
  method: 'patch',
  path: '/v1/channels/{channel}/assets/{id}',
  operationId: 'updateAsset',
  summary: 'Rename or retag a file',
  tags: ['assets'],
  scopes: ['assets:write'],
  params: assetParams,
  body: AssetPatch,
  response: Asset,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/assets/{id}/retire',
  operationId: 'retireAsset',
  summary: 'Retire a file without breaking what references it',
  tags: ['assets'],
  scopes: ['assets:write'],
  params: assetParams,
  body: z.object({ retired: z.boolean() }),
  response: Asset,
  errors: [401, 403, 404, 429],
  idempotent: true,
});

// --- items -------------------------------------------------------------------
//
// The layer between a file and a reward. Creation is explicit — not every
// upload is a collectible — and the bulk builder is what keeps that from
// being tedious.

const itemParams = z.object({ channel: ChannelId, id: z.string() });

route({
  method: 'get',
  path: '/v1/channels/{channel}/items',
  operationId: 'listItems',
  summary: 'The collectibles this channel has defined',
  tags: ['catalog'],
  scopes: ['rewards:read'],
  params: channelParams,
  query: z.object({ limit: z.coerce.number().int().min(1).max(500).optional() }),
  response: z.object({ data: z.array(Item) }),
  errors: [400, 401, 403, 404, 429, 503],
});

route({
  method: 'get',
  path: '/v1/channels/{channel}/items/{id}',
  operationId: 'getItem',
  summary: 'One collectible',
  tags: ['catalog'],
  scopes: ['rewards:read'],
  params: itemParams,
  response: Item,
  errors: [401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/items',
  operationId: 'createItem',
  summary: 'Define a collectible',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: channelParams,
  body: ItemInput,
  response: Item,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'patch',
  path: '/v1/channels/{channel}/items/{id}',
  operationId: 'updateItem',
  summary: 'Edit a collectible',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: itemParams,
  body: ItemPatch,
  response: Item,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/items/{id}/retire',
  operationId: 'retireItem',
  summary: 'Retire a collectible without breaking the copies that exist',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: itemParams,
  body: z.object({ retired: z.boolean() }),
  response: Item,
  errors: [401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/items/preview',
  operationId: 'previewItemBuild',
  // Writes nothing. A build is all-or-nothing, so the only way to learn what
  // it would do is to ask.
  summary: 'What building this batch would produce, and everything wrong with it',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: channelParams,
  body: ItemBuildRequest,
  response: ItemBuildPreview,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/channels/{channel}/items/build',
  operationId: 'buildItems',
  summary: 'Build many collectibles from many files, all of them or none',
  tags: ['catalog'],
  scopes: ['rewards:write'],
  params: channelParams,
  body: ItemBuildRequest,
  response: z.object({ data: z.array(Item) }),
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

/**
 * Rooms.
 *
 * These are PLATFORM routes, not channel routes: a room spans channels, so
 * there is no addressed channel and no channel scope to require. Authority is
 * a room role, read server-side — which is why `scopes` is empty here rather
 * than naming something that would have to mean the same thing on every
 * channel in the room.
 *
 * The exception proves it: putting a channel into a community requires
 * `channel:write` ON THAT CHANNEL, and the path names exactly one.
 */
const roomParams = z.object({ room: RoomId });
/** A room is addressed by id OR handle, so the chat paths accept either. */
const RoomRef = z.string().min(1);
const roomChannelParams = z.object({ room: RoomId, channel: ChannelId });

route({
  method: 'post',
  path: '/v1/rooms',
  operationId: 'createRoom',
  summary: 'Create a community or a saved multistream',
  tags: ['rooms'],
  scopes: [],
  body: RoomInput,
  response: Room,
  errors: [400, 401, 403, 409, 429, 503],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/rooms',
  operationId: 'listMyRooms',
  summary: 'Rooms you own or may edit',
  tags: ['rooms'],
  scopes: [],
  response: z.object({ data: z.array(Room) }),
  errors: [401, 403, 429],
});

route({
  method: 'get',
  path: '/v1/blocks',
  operationId: 'listBlocks',
  summary: 'Who you have blocked',
  tags: ['moderation'],
  scopes: [],
  response: z.object({
    data: z.array(z.object({ uid: z.string(), login: z.string().nullable() })),
  }),
  errors: [401, 429],
});

route({
  method: 'put',
  path: '/v1/blocks/{uid}',
  operationId: 'blockUser',
  summary: 'Stop seeing this person, everywhere',
  tags: ['moderation'],
  scopes: [],
  // PUT, not POST: blocking twice is the same request twice, which is what a
  // client retrying on a flaky connection does.
  response: z.object({}),
  errors: [400, 401, 429],
});

route({
  method: 'delete',
  path: '/v1/blocks/{uid}',
  operationId: 'unblockUser',
  summary: 'Undo a block',
  tags: ['moderation'],
  scopes: [],
  response: z.object({}),
  errors: [401, 429],
});

/**
 * Room chat (ADR 0038), and the moderation that ADR 0042 requires ship with it.
 *
 * Note what is NOT here: anything that moderates a Twitch message. A room's
 * mirrored Twitch chat reaches each browser directly from Twitch and is the
 * streamer's to moderate, so there is no tomo endpoint that could act on one
 * and the absence is the decision, not an omission.
 */
route({
  method: 'get',
  path: '/v1/rooms/{room}/messages',
  operationId: 'listRoomMessages',
  summary: "A room's own recent messages. Not its Twitch chat",
  tags: ['rooms'],
  scopes: [],
  params: roomParams,
  response: z.object({ data: z.array(RoomMessage) }),
  errors: [401, 404, 429],
});

route({
  method: 'post',
  path: '/v1/rooms/{room}/messages',
  operationId: 'postRoomMessage',
  summary: 'Say something to the room',
  tags: ['rooms'],
  scopes: [],
  params: roomParams,
  body: RoomMessageInput,
  response: RoomMessage,
  // 403 is `muted`, which is deliberately not `insufficient_scope`: the
  // credential is fine, and there is no scope an integrator could ask for.
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/messages/{id}',
  operationId: 'deleteRoomMessage',
  summary: 'Remove a message — yours, or anyone’s if you moderate the room',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef, id: RoomMessageId }),
  response: z.object({}),
  errors: [401, 403, 404, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/mutes',
  operationId: 'listRoomMutes',
  summary: 'Who is silenced in this room',
  tags: ['moderation'],
  scopes: [],
  params: roomParams,
  response: z.object({ data: z.array(RoomMute) }),
  errors: [401, 403, 404, 429],
});

route({
  method: 'put',
  path: '/v1/rooms/{room}/mutes/{uid}',
  operationId: 'muteInRoom',
  summary: 'Silence somebody here for a while, optionally ejecting them',
  tags: ['moderation'],
  scopes: [],
  params: z.object({ room: RoomRef, uid: z.string().min(1) }),
  body: RoomMuteInput,
  response: RoomMute,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/mutes/{uid}',
  operationId: 'unmuteInRoom',
  summary: 'Lift a mute early',
  tags: ['moderation'],
  scopes: [],
  params: z.object({ room: RoomRef, uid: z.string().min(1) }),
  response: z.object({}),
  errors: [401, 403, 404, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/roster',
  operationId: 'getRoomRoster',
  summary: 'Who belongs to this room, and which of them are live now',
  tags: ['rooms'],
  // No scope and no session. This is what the front door shows before anybody
  // signs in, and it is why liveness is polled server-side — the browser has
  // no Twitch token until a viewer logs in (ADR 0043).
  scopes: [],
  params: z.object({ room: RoomRef }),
  response: RoomRoster,
  errors: [404, 429],
});

const Followed = z.object({
  login: z.string(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  live: z.boolean(),
  game_name: z.string(),
  viewer_count: z.number().int().min(0),
});

route({
  method: 'get',
  path: '/v1/me/follows',
  operationId: 'listMyFollows',
  summary: 'Twitch channels you follow, and which of them are live',
  tags: ['rooms'],
  scopes: [],
  response: z.object({ data: z.array(Followed) }),
  errors: [401, 429],
});

route({
  method: 'get',
  path: '/v1/me/emotes',
  operationId: 'listMyEmotes',
  summary: 'Emotes you have saved',
  tags: ['emotes'],
  scopes: [],
  response: SavedEmoteList,
  errors: [401, 403, 429],
});

route({
  method: 'put',
  path: '/v1/me/emotes',
  operationId: 'replaceMyEmotes',
  summary: 'Replace the emotes you have saved',
  tags: ['emotes'],
  scopes: [],
  // The whole list, not one entry: the client holds it, edits are rare, and
  // a replace cannot leave half of two concurrent edits behind.
  body: SavedEmoteList,
  response: SavedEmoteList,
  errors: [400, 401, 403, 429],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/me/emote-overrides',
  operationId: 'listMyEmoteOverrides',
  summary: 'Words you have chosen to see drawn differently',
  tags: ['emotes'],
  scopes: [],
  response: EmoteOverrideList,
  errors: [401, 403, 429],
});

route({
  method: 'put',
  path: '/v1/me/emote-overrides',
  operationId: 'replaceMyEmoteOverrides',
  summary: 'Replace the words you have chosen to see drawn differently',
  tags: ['emotes'],
  scopes: [],
  // Whole, like saved emotes, for the same reasons.
  body: EmoteOverrideList,
  response: EmoteOverrideList,
  errors: [400, 401, 403, 429],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/saved-emotes',
  operationId: 'listSenderEmotes',
  summary: 'The saved emotes of people whose messages you are reading',
  tags: ['emotes'],
  scopes: [],
  query: SenderEmotesQuery,
  response: SenderEmotes,
  errors: [400, 401, 403, 429],
});

route({
  method: 'get',
  path: '/v1/me/rooms',
  operationId: 'listMyRecentRooms',
  summary: 'Rooms you have been in lately',
  tags: ['rooms'],
  scopes: [],
  response: z.object({
    data: z.array(z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string().nullable(),
      kind: z.string(),
      channels: z.array(z.string()),
      last_seen_at: z.string(),
    })),
  }),
  errors: [401, 429],
});

route({
  method: 'delete',
  path: '/v1/me/rooms/{room}',
  operationId: 'forgetMyRecentRoom',
  summary: 'Take a room out of the rooms you have been in lately',
  tags: ['rooms'],
  // The room's id, as `/v1/me/rooms` lists it. Forgetting a room you were
  // never in, or one that no longer exists, is the same 204: the answer to
  // "is it off my list" is yes either way. Opening the room again puts it
  // back, which is what a visit log should do.
  scopes: [],
  params: z.object({ room: z.string() }),
  response: z.null(),
  errors: [401, 429],
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/presence',
  operationId: 'leaveRoomPresence',
  summary: 'Say you have left a room, rather than waiting to be noticed',
  tags: ['rooms'],
  // The stream route removes a row when its socket closes, but `finish` runs
  // once and the 25-second lifetime timer usually gets there first — so the
  // close that follows is a no-op and the departure never happens. What is
  // left behind lingers for the 90-second TTL, which is long enough to walk
  // back to the front page and watch yourself sitting in a room you left.
  scopes: [],
  params: z.object({ room: z.string() }),
  response: z.null(),
  errors: [401, 403, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/live',
  operationId: 'roomLiveCounts',
  summary: 'How many of each community\'s roster are live right now',
  tags: ['rooms'],
  // No scope and no session: who is streaming is public on Twitch, and the
  // front page shows it before anybody signs in. Read from the live sweep's
  // snapshot (ADR 0051), so it costs no Twitch call and no Firestore read.
  scopes: [],
  // Comma-separated room ids, capped at thirty like presence.
  query: z.object({ rooms: z.string().optional() }),
  response: RoomLiveCounts,
  errors: [429],
});

route({
  method: 'get',
  path: '/v1/rooms/presence',
  operationId: 'roomPresence',
  summary: 'How many people are in each of these rooms, with a few names',
  tags: ['rooms'],
  // Signed in, for all of it. Occupancy is not public: "how many people are
  // in that room right now" is something a room's occupants have not
  // published, and a count being aggregate does not make it a stranger's
  // business. The stream route already requires a name to see presence, and
  // this is the same information by a different door.
  scopes: [],
  // Comma-separated room ids, capped at thirty — Firestore takes at most
  // that many values in an `in`, and silently answering for a subset would
  // be a page quietly missing half its counts.
  query: z.object({ rooms: z.string().optional() }),
  response: z.object({ data: z.array(RoomPresence) }),
  errors: [401, 403, 429],
});

route({
  method: 'get',
  path: '/v1/communities',
  operationId: 'listCommunities',
  summary: 'Public communities anyone may browse',
  tags: ['rooms'],
  // No scope and no session: the landing page has to show somebody what is
  // here BEFORE they sign in. A front door that demands a login to show you
  // the door is not a front door.
  //
  // Communities only. A personal Multistream is a link somebody chose to
  // send, and listing them would publish rooms whose whole distribution model
  // is that you were given the address.
  scopes: [],
  // `watching` narrows to the communities that include these Twitch logins,
  // which is how the front page answers "who else watches these people".
  query: z.object({ watching: z.string().optional() }),
  response: z.object({ data: z.array(Room) }),
  errors: [429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/stream',
  operationId: 'roomStream',
  summary: 'Server-sent events for one room: presence, room chat, room events',
  tags: ['rooms'],
  scopes: [],
  // `watching` is how an opted-in viewer shares what is on screen. Absent
  // means NOT sharing, which is the default a client gets by doing nothing
  // (ADR 0040); empty means sharing an empty list, a different answer.
  query: z.object({ watching: z.string().optional() }),
  /**
   * Not JSON, and the spec should not pretend otherwise. The response is
   * `text/event-stream` and never ends with a body a client parses once —
   * describing it as an object would send an integrator looking for one.
   */
  response: z.object({}),
  events: {
    hello: z.object({
      room_id: RoomId,
      uid: z.string(),
      resumed_from: z.string().nullable(),
      lifetime_ms: z.number().int(),
    }),
    presence: z.object({ total: z.number().int(), people: z.array(z.object({}).loose()) }),
    chat: RoomMessage,
    chat_deleted: RoomFeedDeleted,
    live: z.object({ members: z.array(z.object({}).loose()) }),
    bye: z.object({ reason: z.string() }),
  },
  errors: [401, 404, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/feed',
  operationId: 'roomFeed',
  summary: 'A community\'s room chat as a public, read-only event stream, for displays',
  tags: ['rooms'],
  // No scope, no session, no key (ADR 0048). Any active COMMUNITY may be read
  // by anybody — an OBS browser source has no cookie and a desktop app has no
  // tomo login. Ad-hoc rooms and the lobby are not served here.
  //
  // Each stream closes itself after `lifetime_ms`; reconnect with the last
  // event id as `Last-Event-ID` and whatever was said in between is sent
  // first. Every event that moves the cursor carries an id, including `hello`,
  // so a quiet stream still resumes rather than restarting.
  scopes: [],
  params: z.object({ room: RoomRef }),
  response: z.object({}),
  events: {
    hello: RoomFeedHello,
    chat: RoomFeedMessage,
    chat_deleted: RoomFeedDeleted,
    bye: z.object({ reason: z.string() }),
  },
  errors: [404, 429],
});

/*
 * A community's own groupings (ADR 0052). Viewers read them through the
 * roster; these two are the admin panel's, for owners and editors.
 */
route({
  method: 'get',
  path: '/v1/rooms/{room}/groupings',
  operationId: 'getRoomGroupings',
  summary: 'A community\'s groupings and who is in each, with who put them there. Owners and editors',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef }),
  response: RoomGroupings,
  errors: [401, 403, 404, 429],
});

route({
  method: 'put',
  path: '/v1/rooms/{room}/groupings',
  operationId: 'putRoomGroupings',
  summary: 'Replace the groupings and the editors\' assignments. What a streamer set for themselves is kept',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef }),
  body: RoomGroupingsInput,
  response: RoomGroupings,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/twitch/categories',
  operationId: 'searchTwitchCategories',
  summary: 'Search Twitch\'s games, for choosing what a community is about. Signed in',
  tags: ['rooms'],
  scopes: [],
  query: z.object({ q: z.string().max(100) }),
  response: TwitchCategoryList,
  errors: [401, 429, 503],
});

/*
 * Discord go-live connections (ADR 0049). Anybody signed in may connect a
 * server they run — the webhook URL is the proof — and manage their own.
 * The community's owners and editors see every connection and may remove any.
 */
route({
  method: 'get',
  path: '/v1/rooms/{room}/discord',
  operationId: 'listRoomDiscordHooks',
  summary: 'Your Discord connections to this community; a manager sees every one. Never a webhook URL',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef }),
  response: DiscordHookList,
  errors: [401, 403, 404, 429],
});

route({
  method: 'post',
  path: '/v1/rooms/{room}/discord',
  operationId: 'connectRoomDiscordHook',
  summary: 'Post this community\'s go-lives to a Discord webhook you made',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef }),
  body: DiscordHookInput,
  response: DiscordHook,
  // 409: already connected here, or a limit reached. 422: Discord does not
  // recognise the webhook — checked on the way in, while its author is looking.
  errors: [400, 401, 403, 404, 409, 422, 429, 503],
});

route({
  method: 'patch',
  path: '/v1/rooms/{room}/discord/{hook}',
  operationId: 'patchRoomDiscordHook',
  summary: 'Change whose go-lives a connection posts, and whether with the stream preview. Only whoever connected it',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef, hook: DiscordHookId }),
  body: DiscordHookPatch,
  response: DiscordHook,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/discord/{hook}',
  operationId: 'removeRoomDiscordHook',
  summary: 'Disconnect a Discord webhook. Whoever connected it, or a manager of the community',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef, hook: DiscordHookId }),
  response: Ok,
  errors: [401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'post',
  path: '/v1/rooms/{room}/discord/{hook}/test',
  operationId: 'testRoomDiscordHook',
  summary: 'Post a test message to one of your Discord connections',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: RoomRef, hook: DiscordHookId }),
  response: DiscordHookTest,
  errors: [401, 403, 404, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}',
  operationId: 'getRoom',
  summary: 'One room. Communities are public; a multistream is not',
  tags: ['rooms'],
  scopes: [],
  params: roomParams,
  response: Room,
  errors: [401, 403, 404, 429],
});

route({
  method: 'patch',
  path: '/v1/rooms/{room}',
  operationId: 'patchRoom',
  summary: 'Rename a room, change its description, or retire it',
  tags: ['rooms'],
  scopes: [],
  params: roomParams,
  body: RoomPatch,
  response: Room,
  errors: [400, 401, 403, 404, 429, 503],
  idempotent: true,
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/channels',
  operationId: 'listRoomChannels',
  summary: 'The channels in a community',
  tags: ['rooms'],
  scopes: [],
  params: roomParams,
  response: z.object({ data: z.array(RoomChannel) }),
  errors: [401, 403, 404, 429],
});

route({
  method: 'put',
  path: '/v1/rooms/{room}/channels/{channel}',
  operationId: 'addRoomChannel',
  summary: 'Put a channel you control into a community',
  tags: ['rooms'],
  // The only room route with a channel scope, and it names ONE channel.
  scopes: ['channel:write'],
  params: roomChannelParams,
  response: Ok,
  errors: [400, 401, 403, 404, 429],
  idempotent: true,
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/channels/{channel}',
  operationId: 'removeRoomChannel',
  summary: 'Take a channel out of a community',
  tags: ['rooms'],
  scopes: [],
  params: roomChannelParams,
  response: Ok,
  errors: [401, 403, 404, 429],
});

route({
  method: 'get',
  path: '/v1/rooms/{room}/search',
  operationId: 'searchRoomChat',
  summary: 'Search a community\u2019s archived chat',
  tags: ['rooms'],
  // Reading a community is public, so no channel scope: authority is the room
  // itself, exactly as for reading the room.
  scopes: [],
  params: roomParams,
  query: z.object({
    q: z.string(),
    days: z.string().optional(),
    limit: z.string().optional(),
  }),
  response: SearchResults,
  // 501 when this deployment keeps no archive, which is configuration rather
  // than a missing endpoint.
  errors: [400, 401, 403, 404, 429, 501],
});

/**
 * Report a problem.
 *
 * NO SCOPES AND NO SIGN-IN, which is the deliberate part. The report this
 * feature was built for is a video pane that will not load, and the viewer
 * who hits that is the least likely person in the room to have an account:
 * whatever is blocking the player may well have blocked the sign-in too.
 * Requiring a session would filter out exactly the reports worth having.
 *
 * `uid` is recorded when there IS one, so a report can be followed up, and
 * left null otherwise rather than refused. 429 is in the list because an open
 * write endpoint has to be, and the limiter is what makes the openness safe.
 */
route({
  method: 'post',
  path: '/v1/bug-reports',
  operationId: 'createBugReport',
  summary: 'Report a problem, with what the browser knew at the time',
  tags: ['support'],
  scopes: [],
  body: BugReportInput,
  response: z.object({ id: z.string() }),
  errors: [400, 429],
});

/**
 * Read them. Platform admins only.
 *
 * A bug report carries a user agent, a path and a screenshot of somebody's
 * screen. That is a viewer's business and the platform's, and nobody else's —
 * so unlike the write, this is the most closed thing in the API.
 */
route({
  method: 'get',
  path: '/v1/bug-reports',
  operationId: 'listBugReports',
  summary: 'Problems people have reported',
  tags: ['support'],
  scopes: [],
  query: PageQuery,
  response: pageOf(BugReport),
  errors: [401, 403, 429],
});

const roomRef = z.object({ room: z.string().min(1) });

/**
 * Who runs this community.
 *
 * Owner-only, not moderator-only. Editing the list of people who can edit the
 * list is the one power that must not be delegated by the people it was
 * delegated to — an editor able to appoint editors is an owner with extra
 * steps, and the room's actual owner would find out afterwards.
 */
route({
  method: 'get',
  path: '/v1/rooms/{room}/admins',
  operationId: 'listRoomAdmins',
  summary: 'Who runs this community, including invitations not yet collected',
  tags: ['rooms'],
  scopes: [],
  params: roomRef,
  response: z.object({ data: z.array(RoomAdmin) }),
  errors: [401, 403, 404, 429],
});

/**
 * Give somebody standing, by the handle an admin actually knows.
 *
 * A login rather than a uid, and the login may belong to somebody who has
 * never signed in — the grant waits on their immutable numeric id and is
 * redeemed at their first login (ADR 0044 §4).
 */
route({
  method: 'put',
  path: '/v1/rooms/{room}/admins',
  operationId: 'addRoomAdmin',
  summary: 'Give somebody standing in this community',
  tags: ['rooms'],
  scopes: [],
  params: roomRef,
  body: RoomAdminInput,
  response: RoomAdmin,
  errors: [400, 401, 403, 404, 429],
});

/*
 * Removal names ONE person, by what cannot change: a member by uid, a waiting
 * grant by Twitch user id. It used to take a handle and resolve it at the
 * moment of removal — which after a rename meant whoever holds that name now,
 * so "remove alice" could strip a stranger's role and leave alice's grant.
 */
route({
  method: 'delete',
  path: '/v1/rooms/{room}/members/{uid}',
  operationId: 'removeRoomMember',
  summary: 'Take somebody\u2019s standing away',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: z.string().min(1), uid: z.string().min(1) }),
  response: Ok,
  errors: [400, 401, 403, 404, 429],
});

route({
  method: 'delete',
  path: '/v1/rooms/{room}/invitations/{twitch_user_id}',
  operationId: 'withdrawRoomInvitation',
  summary: 'Withdraw a grant nobody has collected yet',
  tags: ['rooms'],
  scopes: [],
  params: z.object({ room: z.string().min(1), twitch_user_id: z.string().regex(/^\d{1,20}$/) }),
  response: Ok,
  errors: [400, 401, 403, 404, 429],
});
